import { Bookmark } from "@novorender/data-js-api";

let pendingCapture: ((canvas: HTMLCanvasElement) => void) | null = null;

export function notifyFrameRendered(webglCanvas: HTMLCanvasElement): void {
    if (!pendingCapture) {
        return;
    }
    const cb = pendingCapture;
    pendingCapture = null;
    // Copy pixels synchronously before the framebuffer is cleared on the next draw
    const copy = document.createElement("canvas");
    copy.width = webglCanvas.width;
    copy.height = webglCanvas.height;
    copy.getContext("2d")!.drawImage(webglCanvas, 0, 0);
    cb(copy);
}

function captureNextFrame(): Promise<HTMLCanvasElement> {
    if (pendingCapture) {
        return Promise.reject(new Error("Snapshot capture already in progress"));
    }
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            pendingCapture = null;
            reject(new Error("Snapshot frame capture timed out"));
        }, 5000);
        pendingCapture = (canvas) => {
            clearTimeout(timer);
            resolve(canvas);
        };
    });
}

export function applyGroupStatusToBookmark(bm: Bookmark, groupId: string, status: "hidden" | "frozen"): Bookmark {
    const { explorerState } = bm;
    if (!explorerState?.groups) {
        return bm;
    }
    const { groups } = explorerState;
    const existing = groups.find((g) => g.id === groupId);
    if (existing && existing.status !== status) {
        return {
            ...bm,
            explorerState: {
                ...explorerState,
                groups: groups.map((g) => (g.id === groupId ? { ...g, status } : g)),
            },
        };
    }
    if (!existing) {
        return {
            ...bm,
            explorerState: {
                ...explorerState,
                groups: [...groups, { id: groupId, status }],
            },
        };
    }
    return bm;
}

export const ELEMENT_IDS = Object.freeze({
    renderContainer: "render-container",
    mainCanvas: "main-canvas",
    markupContainer: "markup-container",
    markersContainer: "markers-container",
    mobileTopBar: "mobile-top-bar",
});

export async function createBookmarkImg(canvas: HTMLCanvasElement): Promise<string> {
    const width = canvas.width;
    const height = canvas.height;
    let dx = 0;
    let dy = 0;

    if (height / width < 0.7) {
        dx = width - Math.round((height * 10) / 7);
    } else {
        dy = height - Math.round(width * 0.7);
    }

    const dist = document.createElement("canvas");
    dist.height = 70;
    dist.width = 100;
    const ctx = dist.getContext("2d")!;

    ctx.drawImage(
        canvas,
        Math.round(dx / 2),
        Math.round(dy / 2),
        width - dx,
        height - dy,
        0,
        0,
        dist.width,
        dist.height,
    );

    return dist.toDataURL("image/jpeg");
}

type CreateSnapshotImgInnerOptions = {
    width?: number;
    height?: number;
    type?: "jpeg" | "png";
};

async function createSnapshotImgInner(options: CreateSnapshotImgInnerOptions = {}) {
    // captureNextFrame resolves with a 2D canvas copy of the WebGL frame
    const frame = await captureNextFrame();

    const markupEl = document.getElementById(ELEMENT_IDS.markupContainer);
    if (markupEl) {
        try {
            const svgStr = new XMLSerializer().serializeToString(markupEl);
            // base64 data URL avoids blob: URL restrictions in WKWebView
            const encoded = new TextEncoder().encode(svgStr);
            const binary = Array.from(encoded, (byte) => String.fromCodePoint(byte)).join("");
            const dataUrl = "data:image/svg+xml;base64," + btoa(binary);
            await new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => {
                    frame.getContext("2d")!.drawImage(img, 0, 0, frame.width, frame.height);
                    resolve();
                };
                img.onerror = () => resolve();
                img.src = dataUrl;
            });
        } catch {
            // skip overlay on serialization failure
        }
    }

    const { sx, sy, sw, sh, targetWidth, targetHeight } = positionSnapshot(
        frame.width,
        frame.height,
        options.width,
        options.height,
    );

    const dist = document.createElement("canvas");
    dist.width = targetWidth;
    dist.height = targetHeight;
    dist.getContext("2d")!.drawImage(frame, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
    return dist;
}

export async function createSnapshotImgAsArrayBuffer(
    options: CreateSnapshotImgInnerOptions = {},
): Promise<ArrayBuffer> {
    const dist = await createSnapshotImgInner(options);
    const blob = await new Promise<Blob | null>((resolve) => {
        dist.toBlob(resolve, `image/${options.type || "jpeg"}`);
    });
    if (!blob) {
        throw new Error("Failed to create snapshot blob");
    }
    return await blob.arrayBuffer();
}

export async function createSnapshotImg(options: CreateSnapshotImgInnerOptions = {}) {
    const dist = await createSnapshotImgInner(options);
    return dist.toDataURL(`image/${options.type || "jpeg"}`);
}

// exported for testing
export function positionSnapshot(
    width: number,
    height: number,
    requestedWidth: number | undefined,
    requestedHeight: number | undefined,
) {
    const aspectRatio = width / height;

    let targetWidth = 0;
    let targetHeight = 0;

    if (requestedWidth && requestedHeight) {
        targetWidth = requestedWidth;
        targetHeight = requestedHeight;
    } else if (requestedWidth) {
        targetWidth = requestedWidth;
        targetHeight = Math.round(requestedWidth / aspectRatio);
    } else if (requestedHeight) {
        targetWidth = Math.round(requestedHeight * aspectRatio);
        targetHeight = requestedHeight;
    } else {
        targetWidth = width;
        targetHeight = height;
    }

    let dx = 0;
    let dy = 0;

    if (targetWidth <= 0 || targetHeight <= 0) {
        throw new Error(`Invalid target dimensions: targetWidth=${targetWidth}, targetHeight=${targetHeight}`);
    }
    const targetAspectRatio = targetWidth / targetHeight;

    if (Math.abs(aspectRatio - targetAspectRatio) >= 0.01) {
        if (aspectRatio < targetAspectRatio) {
            dy = height - Math.round(height * (aspectRatio / targetAspectRatio));
        } else {
            dx = width - Math.round(width * (targetAspectRatio / aspectRatio));
        }
    }

    return {
        sx: Math.round(dx / 2),
        sy: Math.round(dy / 2),
        sw: width - dx,
        sh: height - dy,
        targetWidth,
        targetHeight,
    };
}
